import { faX } from "@fortawesome/pro-light-svg-icons";
import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { Box, Stack } from "@mui/material";
import { useState } from "react";

import LikeIcon from "@/components/selection-sidebar/menu-item-icons/LikeIcon";

import { Icon, IconButton } from "../../components/ui-v2";
import { useUpdateFiles } from "../../hooks/useUpdateFiles";
import { SelectionSidebarHeaderMenu } from "./SelectionSidebarHeaderMenu";
import { SelectionSidebarHeaderTitle } from "./SelectionSidebarHeaderTitle";
import { SelectedObject, SidebarContext } from "./types";

interface SelectionSidebarHeaderProps {
  objects: SelectedObject[];
  context?: SidebarContext;
  onCloseDialog?: () => void;
  onReloadObjects?: () => void;
}

export const SelectionSidebarHeader = (props: SelectionSidebarHeaderProps) => {
  const { objects, onCloseDialog, onReloadObjects, context } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>();
  const { updateFiles, result } = useUpdateFiles();

  const shouldShowLikeButton = objects.every(
    (obj) => obj.__typename === "File" || obj.__typename === "Asset",
  );
  const isEveryObjectLiked =
    shouldShowLikeButton && objects.every((obj) => obj.isLiked);
  const shouldShowCloseButton = context
    ? ["session", "drive", "style-gallery"].includes(context)
    : false;

  const handleOpenHeaderMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseHeaderMenu = () => {
    setAnchorEl(undefined);
  };

  const handleLikeObjects = () => {
    if (!shouldShowLikeButton) {
      return;
    }

    const isLiked = !isEveryObjectLiked;

    updateFiles(
      objects.map((file) => file.id),
      { isLiked },
      `${objects.length > 1 ? "All items" : "Item "} successfully ${isLiked ? "liked" : "unliked"}.`,
    );
  };

  const handleObjectsDeleted = () => {
    onReloadObjects?.();
    onCloseDialog?.();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        pl: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {shouldShowCloseButton && (
          <IconButton
            rounded
            sx={{ mr: 2 }}
            variant="quaternary"
            aria-label="Clear selection"
            onClick={onCloseDialog}
          >
            <Icon size="small" icon={faX} />
          </IconButton>
        )}
        <SelectionSidebarHeaderTitle objects={objects} />
      </Stack>
      <Stack direction="row">
        {shouldShowLikeButton && (
          <IconButton
            rounded
            loading={result.fetching}
            variant="quaternary"
            aria-label={isEveryObjectLiked ? "Unlike" : "Like"}
            onClick={handleLikeObjects}
          >
            <LikeIcon isLiked={isEveryObjectLiked} />
          </IconButton>
        )}
        <IconButton
          onClick={handleOpenHeaderMenu}
          variant="quaternary"
          aria-label="More actions"
        >
          <Icon icon={faEllipsis} size="large" />
        </IconButton>
        {context && (
          <SelectionSidebarHeaderMenu
            context={context}
            anchorEl={anchorEl}
            objects={objects}
            onClose={handleCloseHeaderMenu}
            onDeleted={handleObjectsDeleted}
          />
        )}
      </Stack>
    </Box>
  );
};
